import React from 'react'
import { cn } from '../../utils/cn'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Checkbox label
   */
  label?: string

  /**
   * Error message
   */
  error?: string

  /**
   * Helper text
   */
  helperText?: string

  /**
   * Description text (below label)
   */
  description?: string
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      description,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`
    const hasError = !!error

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-3">
          <div className="flex items-center h-5">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              className={cn(
                'w-4 h-4 rounded transition-colors cursor-pointer',
                'border-2 focus:ring-2 focus:ring-offset-0',

                // Normal state
                !hasError && [
                  'border-soft-tan',
                  'checked:bg-avocado-green checked:border-avocado-green',
                  'focus:ring-toast-gold',
                ],

                // Error state
                hasError && [
                  'border-coral-pop',
                  'checked:bg-coral-pop checked:border-coral-pop',
                  'focus:ring-coral-pop',
                ],

                // Disabled state
                disabled && 'opacity-50 cursor-not-allowed',

                className
              )}
              disabled={disabled}
              aria-invalid={hasError}
              aria-describedby={
                error ? `${checkboxId}-error` : helperText ? `${checkboxId}-helper` : undefined
              }
              {...props}
            />
          </div>

          {(label || description) && (
            <div className="flex flex-col gap-1">
              {label && (
                <label
                  htmlFor={checkboxId}
                  className={cn(
                    'text-sm font-medium cursor-pointer',
                    hasError ? 'text-coral-pop' : 'text-deep-charcoal',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {label}
                </label>
              )}

              {description && (
                <p className={cn(
                  'text-xs',
                  hasError ? 'text-coral-pop/70' : 'text-deep-charcoal/60',
                  disabled && 'opacity-50'
                )}>
                  {description}
                </p>
              )}
            </div>
          )}
        </div>

        {error && (
          <p id={`${checkboxId}-error`} className="text-xs text-coral-pop ml-7">
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={`${checkboxId}-helper`} className="text-xs text-deep-charcoal/60 ml-7">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
